import React, { useEffect, useRef, useState } from "react";
import { sectionFrame } from "../../config/site";
import { speakerTrackTabs, speakersByTab, type TrackTab } from "../../content/site";
import { trackEvent } from "../../lib/analytics";
import { SectionHeading } from "../ui/SectionHeading";
import { SpeakerCard } from "../ui/SpeakerCard";
import { TabBar } from "../ui/TabBar";

type SpeakersSectionProps = {
    activeTab: TrackTab;
    onChange: (tab: TrackTab) => void;
};

export function SpeakersSection({ activeTab, onChange }: SpeakersSectionProps) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const mobileMenuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            if (!mobileMenuRef.current?.contains(event.target as Node)) {
                setIsMobileMenuOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    return (
        <section id="speakers" className="px-4 pt-[120px] sm:px-6 max-md:pt-[84px]">
            <div className={sectionFrame}>
                <SectionHeading label="Спикеры" title="50+ экспертов выступят за один день" />
                <div className="hidden max-lg:mb-6 max-lg:block">
                    <div ref={mobileMenuRef} className="relative w-full">
                        <button
                            type="button"
                            aria-haspopup="listbox"
                            aria-expanded={isMobileMenuOpen}
                            onClick={() => setIsMobileMenuOpen((current) => !current)}
                            className="flex h-[48px] w-full items-center justify-between rounded-[153.86px] border-[1.54px] border-accent bg-transparent px-6 py-3 text-[16px] leading-[1] font-normal tracking-[0] text-accent"
                            style={{
                                fontFamily:
                                    '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                            }}
                        >
                            <span>{activeTab}</span>
                            <span
                                className={[
                                    "text-accent transition-transform duration-200",
                                    isMobileMenuOpen ? "rotate-180" : "",
                                ].join(" ")}
                                aria-hidden="true"
                            >
                                <svg
                                    width="14"
                                    height="8"
                                    viewBox="0 0 14 8"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1 1L7 7L13 1"
                                        stroke="currentColor"
                                        strokeWidth="1.54"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                        </button>
                        {isMobileMenuOpen ? (
                            <div
                                role="listbox"
                                aria-label="Треки"
                                className="absolute left-0 top-[calc(100%+8px)] z-20 flex w-full flex-col overflow-hidden rounded-[24px] border-[1.54px] border-accent bg-black/95 p-2 shadow-[0_18px_48px_rgba(0,0,0,0.45)]"
                            >
                                {speakerTrackTabs.map((tab) => {
                                    const isActive = tab === activeTab;

                                    return (
                                        <button
                                            key={tab}
                                            type="button"
                                            role="option"
                                            aria-selected={isActive}
                                            onClick={() => {
                                                trackEvent("speaker_track_select", {
                                                    tab,
                                                    source: "mobile_selector",
                                                });
                                                onChange(tab);
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className={[
                                                "flex min-h-[44px] items-center rounded-[18px] px-4 py-3 text-left text-[16px] leading-[1] font-normal tracking-[0] transition-colors",
                                                isActive ? "bg-accent text-black" : "text-white",
                                            ].join(" ")}
                                            style={{
                                                fontFamily:
                                                    '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                            }}
                                        >
                                            {tab}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="max-lg:hidden">
                    <TabBar
                        active={activeTab}
                        onChange={(tab) => {
                            trackEvent("speaker_track_select", { tab, source: "desktop_tabs" });
                            onChange(tab);
                        }}
                        tabs={speakerTrackTabs}
                        variant="program"
                    />
                </div>
                <div className="flex flex-col gap-6 max-lg:gap-4">
                    {speakersByTab[activeTab].map((speaker, index) => (
                        <SpeakerCard
                            key={`${speaker.speakers[0]?.name ?? "speaker"}-${index}`}
                            {...speaker}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
